import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { sections } from '../mock';

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY;
  window.scrollTo({ top: y, behavior: 'smooth' });
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState('home');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      let current = 'home';
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= 140) current = s.id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleNav = (id) => {
    setMobileOpen(false);
    // Allow overflow reset before scrolling
    setTimeout(() => scrollToId(id), 50);
  };

  const inkClass = scrolled || mobileOpen ? 'text-[#2b2a27]' : 'text-white';
  const borderClass =
    scrolled || mobileOpen
      ? 'border-[#2b2a27] hover:bg-[#2b2a27] hover:text-[#f4f1eb]'
      : 'border-white hover:bg-white hover:text-[#2b2a27]';

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-500 ${
          scrolled || mobileOpen
            ? 'bg-[#f4f1eb]/95 backdrop-blur-sm border-b border-[#c9c3b6]/40'
            : 'bg-transparent'
        }`}
      >
        <div
          className={`flex items-center justify-between px-6 md:px-10 h-20 ${inkClass}`}
        >
          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 -ml-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileOpen ? (
              <X strokeWidth={1} size={22} />
            ) : (
              <Menu strokeWidth={1} size={22} />
            )}
          </button>

          <button
            onClick={() => handleNav('home')}
            className="font-serif-display text-[13px] md:text-[15px] tracking-[0.42em] uppercase select-none hover:opacity-80 transition-opacity lg:ml-0"
          >
            Riva Build Group
          </button>

          <nav className="hidden lg:flex items-center gap-7">
            {sections.slice(1, -1).map((s) => (
              <button
                key={s.id}
                onClick={() => handleNav(s.id)}
                className={`font-sans-ui text-[10px] tracking-[0.28em] pb-1 border-b transition-colors ${
                  active === s.id
                    ? 'border-current opacity-100'
                    : 'border-transparent opacity-80 hover:opacity-100'
                }`}
              >
                {s.label}
              </button>
            ))}
          </nav>

          <button
            onClick={() => handleNav('contact')}
            className={`font-sans-ui text-[10px] md:text-[11px] tracking-[0.28em] px-4 md:px-5 py-2 border transition-colors ${borderClass}`}
          >
            Enquire
          </button>
        </div>
      </header>

      {/* Mobile slide-down panel */}
      <div
        className={`fixed inset-0 top-20 z-30 bg-[#f4f1eb] transition-all duration-500 lg:hidden ${
          mobileOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
      >
        <nav className="flex flex-col px-8 py-10 gap-6">
          {sections.slice(1, -1).map((s, i) => (
            <button
              key={s.id}
              onClick={() => handleNav(s.id)}
              className={`font-serif-display text-[32px] lowercase text-left text-[#2b2a27] transition-transform duration-500 ${
                mobileOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
              }`}
              style={{ transitionDelay: `${mobileOpen ? i * 60 : 0}ms` }}
            >
              {s.label.toLowerCase()}
            </button>
          ))}
          <div className="pt-10 mt-4 border-t border-[#c9c3b6]/70">
            <div className="font-sans-ui text-[10px] tracking-[0.3em] text-[#5a5852] mb-3">
              Contact
            </div>
            <a
              href="mailto:info@rivabuildgroup.com"
              className="font-serif-display italic text-[18px] text-[#2b2a27] underline-anim"
            >
              info@rivabuildgroup.com
            </a>
            <div className="mt-3 text-[13px] text-[#5a5852]">
              9200 Sunset Boulevard, West Hollywood, CA 90069
            </div>
          </div>
        </nav>
      </div>
    </>
  );
}

export { scrollToId };
